import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { useEffect, useState, type JSX } from "react";
import { TextField, Typography } from "@platform/design-system";

import { AGE_BAND_AT_MOST } from "../../constants";

interface AgeBandRowProps {
  band: { lower: number; upper: number };
  index: number;
  isFirstRow: boolean;
  isLastRow: boolean;
  isCriticalIllness: boolean;
  isAgeBanded: boolean;
  onSingleAgeBandChange: (index: number, value: string) => void;
  onAgeBandChange: (
    index: number,
    field: "lower" | "upper",
    value: string,
  ) => void;
  onRemoveAgeBand: (index: number) => void;
}

/**
 * A TextField that maintains local string state during editing, only
 * committing the raw string value to the parent on blur (parsing happens in the parent).
 * This prevents cursor loss caused by immediate parseInt on every keystroke.
 */
function AgeBandTextField({
  numericValue,
  onCommit,
  startAdornment,
  endAdornment,
  placeholder,
}: {
  numericValue: number;
  onCommit: (value: string) => void;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  placeholder?: string;
}): JSX.Element {
  const [localValue, setLocalValue] = useState(String(numericValue));

  // Sync from parent when the numeric value changes externally (e.g. generate)
  useEffect(() => {
    setLocalValue(String(numericValue));
  }, [numericValue]);

  return (
    <TextField
      type="number"
      size="sm"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onCommit(localValue)}
      sx={{ width: 100 }}
      placeholder={placeholder}
      InputProps={{
        ...(startAdornment != null && {
          startAdornment: (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ),
        }),
        ...(endAdornment != null && {
          endAdornment: (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ),
        }),
      }}
    />
  );
}

export function AgeBandRow({
  band,
  index,
  isFirstRow,
  isLastRow,
  isCriticalIllness,
  isAgeBanded,
  onSingleAgeBandChange,
  onAgeBandChange,
  onRemoveAgeBand,
}: AgeBandRowProps): JSX.Element {
  // For critical illness, show single-age input (lower === upper)
  if (isCriticalIllness && isAgeBanded) {
    return (
      <Box
        data-testid={`age-band-row-${index}`}
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <AgeBandTextField
          numericValue={band.lower}
          onCommit={(value) => onSingleAgeBandChange(index, value)}
          placeholder="Age"
        />
        <IconButton
          size="small"
          onClick={() => onRemoveAgeBand(index)}
          sx={{
            opacity: 0,
            transition: "opacity 0.2s",
            ".MuiBox-root:hover > &": { opacity: 1 },
          }}
        >
          <RemoveIcon fontSize="small" color="error" />
        </IconButton>
      </Box>
    );
  }

  const renderTextField = (): JSX.Element => {
    // The first band has no lower bound and the last no upper bound, so each
    // shows the one age it does carry: "≤19" covers everyone up to 19, "75+"
    // everyone from 75 on.
    if (isFirstRow) {
      return (
        <AgeBandTextField
          numericValue={band.upper}
          onCommit={(value) => onAgeBandChange(index, "upper", value)}
          startAdornment={AGE_BAND_AT_MOST}
        />
      );
    }
    if (isLastRow) {
      return (
        <AgeBandTextField
          numericValue={band.lower}
          onCommit={(value) => onAgeBandChange(index, "lower", value)}
          endAdornment="+"
        />
      );
    }
    return (
      <>
        <AgeBandTextField
          numericValue={band.lower}
          onCommit={(value) => onAgeBandChange(index, "lower", value)}
        />
        <Typography>to</Typography>
        <AgeBandTextField
          numericValue={band.upper}
          onCommit={(value) => onAgeBandChange(index, "upper", value)}
        />
      </>
    );
  };

  return (
    <Box
      data-testid={`age-band-row-${index}`}
      sx={{ display: "flex", alignItems: "center", gap: 1 }}
    >
      {renderTextField()}
      <IconButton
        size="small"
        onClick={() => onRemoveAgeBand(index)}
        sx={{
          opacity: 0,
          transition: "opacity 0.2s",
          ".MuiBox-root:hover > &": { opacity: 1 },
        }}
      >
        <RemoveIcon fontSize="small" color="error" />
      </IconButton>
    </Box>
  );
}
